import { useId, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

import { Alert, controlClass, FieldShell, mutedTextClass } from '@/components/layout/ui'
import { cn } from '@/lib/cn'

/**
 * The three controls the signed-out screens add to the shared set.
 *
 * They are composed from `FieldShell` and `controlClass` rather than styled
 * from scratch, so an auth input is the same object as an input anywhere else
 * in the app — it just has something living inside its border.
 */

/** The one link treatment the signed-out screens use. */
export const authLinkClass = 'rounded font-medium text-brand-600 hover:underline dark:text-brand-400'

export interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string
  hint?: ReactNode
  error?: string
}

/**
 * A password input with a show/hide button inside the field.
 *
 * The button's accessible name is the action, not the state, and it changes
 * with the field: a screen reader user hears "Show password" and, once the
 * password is revealed, "Hide password". `aria-controls` ties it to the input
 * it governs and `aria-pressed` reports the current state.
 *
 * It stays in the tab order on purpose. Reaching the toggle from the keyboard
 * is exactly what someone who cannot see what they typed needs, and it sits
 * between the field and the submit button, where it does not interrupt a
 * password manager filling the form.
 */
export function PasswordField({ label, hint, error, className, ...rest }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false)

  return (
    <FieldShell label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <div className="relative">
          <input
            {...rest}
            id={id}
            type={visible ? 'text' : 'password'}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(controlClass, 'pr-11', className)}
          />
          <button
            type="button"
            onClick={() => setVisible((shown) => !shown)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-controls={id}
            aria-pressed={visible}
            className={cn(
              'absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg',
              'text-ink-400 transition hover:text-ink-700 dark:hover:text-ink-200',
            )}
          >
            <EyeIcon off={visible} />
          </button>
        </div>
      )}
    </FieldShell>
  )
}

/** An open eye, or one with a stroke through it once the password is showing. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" fill="none" aria-hidden="true">
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.6" />
      {off ? <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /> : null}
    </svg>
  )
}

/** A native checkbox with a clickable label, for "Keep me signed in". */
export function Checkbox({
  label,
  checked,
  onChange,
  hint,
  name,
}: {
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  hint?: ReactNode
  name?: string
}) {
  const id = useId()
  const hintId = `${id}-hint`
  return (
    <div>
      <div className="flex items-center gap-2.5">
        <input
          id={id}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-describedby={hint ? hintId : undefined}
          className="size-4 shrink-0 rounded border-ink-300 accent-brand-600"
        />
        <label htmlFor={id} className="text-sm select-none">
          {label}
        </label>
      </div>
      {hint ? (
        <p id={hintId} className={cn('mt-1.5 ml-[26px] text-xs', mutedTextClass)}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The form-level error region.
 *
 * The announcing is done by the `Alert`, which carries role="alert": inserting
 * it is what a screen reader reports, and replacing its text — the credentials
 * message giving way to the rate-limit one — is reported again, both without
 * moving focus away from the field being corrected. The wrapper around it is
 * layout only (`empty:hidden` keeps the form's vertical rhythm when there is
 * nothing to say); it is not itself a live region, so do not move the message
 * out of the Alert and expect the wrapper to announce it.
 */
export function FormError({ message }: { message?: string | null }) {
  return <div className="empty:hidden">{message ? <Alert tone="danger">{message}</Alert> : null}</div>
}
